import { PlusIcon } from 'lucide-react'
import { useId, useState } from 'react'

import { Button, Input } from '@/components/ui'

export const AddRow = ({
  onAdd,
  blockedReason,
}: {
  onAdd: (input: { date: string; description: string; amount: number }) => void
  // Set when the row cannot be added yet, e.g. the cycle has no account to file it under.
  blockedReason?: string
}) => {
  const fieldId = useId()
  const [date, setDate] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')

  const parsedAmount = Number(amount)
  const canAdd =
    !blockedReason &&
    date !== '' &&
    description.trim() !== '' &&
    !Number.isNaN(parsedAmount)

  const submit = () => {
    if (!canAdd) return
    onAdd({ date, description: description.trim(), amount: parsedAmount })
    setDate('')
    setDescription('')
    setAmount('')
  }

  const submitOnEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') submit()
  }

  const labelClass =
    'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'

  return (
    <div className="mt-3 rounded-lg border bg-muted/30 p-3">
      <p className="mb-3 text-xs font-semibold text-foreground">
        Add a transaction
        {blockedReason && (
          <span className="ml-1.5 font-normal text-muted-foreground">
            {blockedReason}
          </span>
        )}
      </p>
      <div className="flex flex-wrap items-end gap-2.5">
        <label htmlFor={`${fieldId}-date`} className="flex flex-col gap-1.5">
          <span className={labelClass}>Date</span>
          <Input
            id={`${fieldId}-date`}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            onKeyDown={submitOnEnter}
            className="h-9 w-[9.5rem] bg-background"
          />
        </label>
        <label
          htmlFor={`${fieldId}-description`}
          className="flex min-w-48 flex-1 flex-col gap-1.5"
        >
          <span className={labelClass}>Description</span>
          <Input
            id={`${fieldId}-description`}
            placeholder="What was this transaction?"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            onKeyDown={submitOnEnter}
            className="h-9 bg-background"
          />
        </label>
        <label htmlFor={`${fieldId}-amount`} className="flex flex-col gap-1.5">
          <span className={labelClass}>Amount</span>
          <Input
            id={`${fieldId}-amount`}
            type="number"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            onKeyDown={submitOnEnter}
            className="h-9 w-32 bg-background text-right tabular-nums"
          />
        </label>
        <Button
          theme="default"
          size="sm"
          disabled={!canAdd}
          className="h-9 cursor-pointer px-4"
          onClick={submit}
        >
          <PlusIcon className="size-4" />
          Add
        </Button>
      </div>
    </div>
  )
}
